import React, { useState, useEffect } from 'react';
import Plotly from 'react-plotly.js';
import { getChartTitle, getChartAxis, getChartLegend } from '../../utils/chartColors';

const LineChartWithLegend = ({ 
  data, 
  title, 
  xKey, 
  yKeys, 
  colors = ['#f97316', '#3b82f6', '#8b5cf6'],
  showLegend = true,
  formatCurrency = false,
  xLabel = 'Date',
  yLabel = 'Value',
  logScale = false,
  fullHeight = false,
  legendLabels = null
}) => {
  const [themeKey, setThemeKey] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Listen for theme changes and window resize
  useEffect(() => {
    const checkThemeChange = () => {
      setThemeKey(prev => prev + 1);
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setThemeKey(prev => prev + 1); // Force re-render on resize
    };

    // Listen for DOM mutations (theme class changes)
    const observer = new MutationObserver(checkThemeChange);
    const landingPage = document.querySelector('.landing-page');
    if (landingPage) {
      observer.observe(landingPage, { attributes: true, attributeFilter: ['class'] });
    }

    // Listen for window resize
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div>Loading...</div>;
  }

  // Prepare data for Plotly
  const plotData = yKeys.map((yKey, index) => {
    const values = data.map(d => d[yKey] || 0);
    const colorConfig = colors[index] || colors[0];
    const color = typeof colorConfig === 'object' ? colorConfig.color : colorConfig;
    const dash = typeof colorConfig === 'object' ? colorConfig.dash : 'solid';
    
    return {
      type: 'scatter',
      mode: 'lines',
      name: legendLabels && legendLabels[index] ? legendLabels[index] : yKey.replace(/_/g, ' ').toUpperCase(),
      x: data.map(d => new Date(d[xKey])),
      y: values,
      line: { 
        color: color, 
        width: 3,
        dash: dash
      },
      hovertemplate: `<b>%{fullData.name}</b><br>Date: %{x}<br>Value: ${formatCurrency ? '$%{y:,.0f}' : '%{y}'}<extra></extra>`
    };
  });

  // Sort plotData alphabetically by name for consistent legend ordering
  if (showLegend && plotData.length > 1) {
    plotData.sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });
  }

  const isMobile = windowWidth <= 768;
  
  const layout = {
    title: getChartTitle(title),
    showlegend: showLegend,
    legend: {
      ...getChartLegend(yKeys.length, windowWidth),
      traceorder: 'normal' // Use the order of traces in the data array
    },
    xaxis: getChartAxis(xLabel, true),
    yaxis: {
      ...getChartAxis(yLabel, false),
      type: logScale ? 'log' : 'linear'
    },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    margin: {
      l: 80,
      r: isMobile ? 20 : 100,
      t: 80,
      b: isMobile ? (yKeys.length > 6 ? 140 : 120) : 40
    },
    height: 400,
    autosize: true
  };

  const config = {
    displayModeBar: false,
    responsive: true
  };

  return (
    <div style={{ width: '100%', height: fullHeight ? '100%' : '400px' }}>
      <Plotly
        key={themeKey}
        data={plotData}
        layout={layout}
        config={{
          ...config,
          responsive: true,
          useResizeHandler: true
        }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler={true}
      />
    </div>
  );
};

export default LineChartWithLegend;
